import React, { useState, useEffect } from "react";
import { Col, Row } from "reactstrap";
import { useHistory } from "react-router-dom";
import {
	linkUACDealer,
	getDealerInsuranceInfo,
} from "api/DealerIntegrationApi";
import { useFromUser } from "stores/LocalStorageHelper";
import {
	failAlert,
	saveSuccessAlert,
	contactUAC,
	dealerBondAlert,
} from "utils/alertUtils";
import FlooringRatioTable from "./FlooringRatioTable";
import SignUps from "../../settings/subFeatures/settingsTabs/signUp/SignUps";
import useCurrentDealerLinkStore from "stores/DealerLinkStore";
import shallow from "zustand/shallow";
import ArticleCard from "./ArticleCard";
import useIsOwnerOrAdmin from "../../settings/subFeatures/settingsTabs/userManagement/useIsOwnerOrAdmin";
import { DC_CompanyName } from "../../../config";
import dayjs from "dayjs";

const SESSION_STORAGE_FLAG = "DEALER_BOND_ALERT";

const shouldShowDealerBondAlert = () => {
	return sessionStorage.getItem(SESSION_STORAGE_FLAG) !== "true";
};

const DashboardSection = () => {
	const history = useHistory();
	const { uacdealerID } = useCurrentDealerLinkStore((state) => state, shallow);
	const locationID = useFromUser("locationID");
	const taxID = useFromUser("taxID");
	const [uacc, setUacc] = useState(() => {
		const partnerSend = JSON.parse(sessionStorage.getItem("dealerPartnerSend"));
		return partnerSend?.useUACC || 0;
	});
	const isOwnerOrAdmin = useIsOwnerOrAdmin();

	// Check dealer bond alert on component mount
	useEffect(() => {
		const checkDealerBondAlert = async () => {
			if (shouldShowDealerBondAlert()) {
				try {
					const insuranceRes = await getDealerInsuranceInfo(locationID);
					const dlrInfo = insuranceRes.data.content;

					if (
						dlrInfo.dbiExpDate === null ||
						dlrInfo.dbiExpDate === "" ||
						!dayjs(dlrInfo.dbiExpDate).isValid() ||
						dlrInfo.buiExpDate === null ||
						dlrInfo.buiExpDate === "" ||
						!dayjs(dlrInfo.buiExpDate).isValid()
					) {
						dealerBondAlert(history);
					} else if (
						dayjs(dlrInfo.dbiExpDate).isBefore(dayjs()) ||
						dayjs(dlrInfo.buiExpDate).isBefore(dayjs())
					) {
						dealerBondAlert(
							history,
							"Dealer Insurance info is expired, please update..."
						);
					}
				} catch (err) {
					console.error("Error checking dealer insurance info:", err);
				}
			}
		};

		checkDealerBondAlert();
	}, [locationID, history]);

	const toggleUacApi = () => {
		if (!taxID || taxID === "") {
			failAlert(
				"No taxID found, contact " + DC_CompanyName + " to add taxID..."
			);
		} else {
			console.log(taxID);
			linkUACDealer(locationID).then(
				(res) => {
					let link = JSON.parse(sessionStorage.getItem("dealerPartnerLink"));
					link.uacdealerID = res.data.UaccDealerId;
					sessionStorage.setItem("dealerPartnerLink", JSON.stringify(link));
					saveSuccessAlert("UAC linked!");
				},
				(err) => {
					contactUAC(err.response.data.message);
				}
			);
		}
	};

	return (
		<>
			<br />
			{isOwnerOrAdmin && <FlooringRatioTable />}
			<Row className="mt-3">
				<Col md="6" className="mt-3">
					<div className="px-3 w-100">
						<h3>
							<span aria-label="gear emoji" role="img">
								⚙️{" "}
							</span>
							Featured Integrations
						</h3>
						<hr className="mb-2" style={{ width: "100%" }} />
					</div>
					<SignUps
						showUAC={uacc === 1 && uacdealerID.length > 0}
						toggleUacApi={toggleUacApi}
					/>
				</Col>
				<Col md="6" className="mt-3" style={{ borderLeft: "1px solid #ddd" }}>
					<div className="px-3 w-100">
						<h3>
							<span aria-label="paper emoji" role="img">
								📄{" "}
							</span>
							Latest Tips & Tricks
						</h3>
						<hr className="mb-2" style={{ width: "100%" }} />
					</div>
					<div className="d-flex flex-wrap justify-content-center">
						<ArticleCard
							title="Mitigating Dealership Risk as Uninsured Driver Rates Climb"
							description=""
							image="https://modives.com/wp-content/uploads/2024/08/Uninsured-Driver-Rates-Climb.jpg.webp"
							link="https://modives.com/blog/uninsured-driver-rates-climb/"
						/>
						<ArticleCard
							title="Eliminating Manual Processes in Auto Insurance Tracking"
							description=""
							image="https://modives.com/wp-content/uploads/2024/10/Verifacto-Graphic.png.webp"
							link="https://modives.com/blog/eliminating-manual-processes-in-auto-insurance-tracking/"
						/>
						<ArticleCard
							title="How Much Is Auto Insurance Verification Costing You?"
							description=""
							image="https://modives.com/wp-content/uploads/2024/09/How-Much-Is-Auto-Insurance-Verification-Costing-You.png.webp"
							link="https://modives.com/blog/cost-of-auto-insurance-verification/"
						/>
						<ArticleCard
							title="Auto Insurance Monitoring: De-Risking Your Dealership in a Rocky Market"
							description=""
							image="https://modives.com/wp-content/uploads/2024/09/pexels-kampus-8636597-scaled.jpg.webp"
							link="https://modives.com/blog/auto-insurance-monitoring-de-risking-your-dealership-in-a-rocky-market/"
						/>
						<ArticleCard
							title="Navigating the Latest CAR Regulations: A Comprehensive Guide for Dealerships"
							description="Explore the impact of new CAR regulations on auto dealerships and how to stay compliant. Essential insights for modern auto business management."
							image="https://dealerclick.com/static/4819786fde386ad8807d87fba4599a4d/fb23b/what-new-car-rules-mean-for-auto-dealerships.webp"
							link="https://dealerclick.com/blog/what-new-car-rules-mean-for-auto-dealerships/"
						/>
						<ArticleCard
							title="Automotive Market Trends for 2024"
							description="Discover 2024's automotive trends, evolving sales, electric vehicles (EVs) growth, and the impact of consumer preferences and technology in retail."
							image="https://dealerclick.com/static/d588b701e685a3110761bccc26e380b2/fb23b/automotive-market-trends-2024.webp"
							link="https://dealerclick.com/blog/automotive-market-trends-2024/"
						/>
						<ArticleCard
							title="Enhancing F&I Sales: Six Proven Strategies for Success"
							description="Discover 2024's automotive trends, evolving sales, electric vehicles (EVs) growth, and the impact of consumer preferences and technology in retail."
							image="https://dealerclick.com/static/50a7202eec250d91888de156c6abe3b1/fb23b/6-tips-to-sell-more-f-and-i-products.webp"
							link="https://dealerclick.com/blog/6-tips-to-sell-more-f-and-i-products/"
						/>
						<ArticleCard
							title="Automotive Market Trends for 2024"
							description="Discover 2024's automotive trends, evolving sales, electric vehicles (EVs) growth, and the impact of consumer preferences and technology in retail."
							image="https://dealerclick.com/static/d588b701e685a3110761bccc26e380b2/fb23b/automotive-market-trends-2024.webp"
							link="https://dealerclick.com/blog/automotive-market-trends-2024/"
						/>
					</div>
				</Col>
			</Row>
		</>
	);
};

export default DashboardSection;
